<div class="ec2-launch-backdrop" (click)="requestClose()">
  <div class="ec2-launch-modal" (click)="$event.stopPropagation()">
    <div class="ec2-launch-modal__header">
      <div class="ec2-launch-modal__title-wrap">
        <div class="ec2-launch-modal__badge">
          <i class="fa fa-server"></i>
        </div>
        <div>
          <div class="ec2-launch-modal__title">Request Ec2 Instance</div>
          <div class="ec2-launch-modal__subtitle">
            Create a new EC2 instance by choosing an image, compute capacity, network configuration, and storage options.
          </div>
        </div>
      </div>

      <button
        type="button"
        class="ec2-launch-modal__close"
        [disabled]="savingRequest || loading.createSecurityGroup"
        (click)="requestClose()"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div class="ec2-launch-modal__stepper">
      <div
        *ngFor="let stepLabel of wizardStepLabels; let i = index"
        class="ec2-launch-step"
        [ngClass]="{
          'is-active': isWizardStepActive(i),
          'is-complete': isWizardStepComplete(i)
        }"
      >
        <div class="ec2-launch-step__circle">
          <ng-container *ngIf="isWizardStepComplete(i); else wizardStepNumber">
            <i class="fa fa-check"></i>
          </ng-container>
          <ng-template #wizardStepNumber>{{ i + 1 }}</ng-template>
        </div>
        <div class="ec2-launch-step__label">{{ stepLabel }}</div>
      </div>
    </div>

    <div #ec2LaunchModalBody class="ec2-launch-modal__body">
      <div *ngIf="wizardStep === 1" class="ec2-launch-section">
        <div class="ec2-launch-section__title">Choose the operating system and image</div>
        <div class="ec2-launch-section__subtitle">
          Start with the platform details, then pick the AMI that should be used for the request.
        </div>

        <div class="ec2-launch-context">
          <div class="ec2-context-chip">
            <span>Account</span>
            <strong>{{ accountId || "-" }}</strong>
          </div>
          <div class="ec2-context-chip">
            <span>Region</span>
            <strong>{{ regionId || "-" }}</strong>
          </div>
        </div>

        <div class="ec2-launch-grid">
          <div class="form-item ec2-launch-grid__full">
            <label>EC2 Launch Justification <span class="required-star">*</span></label>
            <textarea
              class="form-control ec2-textarea"
              [ngClass]="{
                'field-invalid':
                  wizardSubmitAttempted &&
                  !(form.ec2_launch_justification || '').toString().trim()
              }"
              [(ngModel)]="form.ec2_launch_justification"
              placeholder="Why do you need this instance?"
              rows="3"
            ></textarea>
            <div
              *ngIf="
                wizardSubmitAttempted &&
                !(form.ec2_launch_justification || '').toString().trim()
              "
              class="field-error"
            >
              Launch justification is required.
            </div>
          </div>

          <div class="form-item ec2-launch-grid__full">
            <label>
              Operating System <span class="required-star">*</span>
              <span *ngIf="loading.os" class="tiny-loader"></span>
            </label>
            <div class="ec2-option-grid">
              <button
                type="button"
                *ngFor="let os of options.os"
                class="ec2-option-card"
                [ngClass]="{ 'ec2-option-card--selected': hasSelectedOperatingSystem(os) }"
                (click)="onOperatingSystemSelect(os)"
              >
                <span class="ec2-option-card__title">{{ os }}</span>
                <span class="ec2-option-card__meta">{{ getOperatingSystemDescription(os) }}</span>
              </button>
            </div>
            <div
              *ngIf="wizardSubmitAttempted && !(form.operating_system || '').toString().trim()"
              class="field-error"
            >
              Operating system is required.
            </div>
          </div>

          <div class="form-item">
            <label>
              AMI Id <span class="required-star">*</span>
              <span *ngIf="loading.amis" class="tiny-loader"></span>
            </label>
            <ng-select #amiSelect dropdownPosition="bottom"
              class="instance-type-select"
              [items]="options.amis"
              bindLabel="ami_name"
              bindValue="ami_id"
              [(ngModel)]="form.ami_id"
              (open)="onLaunchModalDropdownOpen(amiSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select AMI"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted && !(form.ami_id || '').toString().trim()
              }"
            >
              <ng-template ng-option-tmp let-item="item">
                <div class="ec2-option-row">
                  <div class="ec2-option-row__title">{{ item.ami_name }}</div>
                  <div class="ec2-option-row__meta">{{ item.ami_id }}</div>
                </div>
              </ng-template>
            </ng-select>
            <div
              *ngIf="wizardSubmitAttempted && !(form.ami_id || '').toString().trim()"
              class="field-error"
            >
              AMI is required.
            </div>
          </div>

          <div class="form-item">
            <label>Architecture <span class="required-star">*</span></label>
            <ng-select #architectureSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.architecture"
              (open)="onLaunchModalDropdownOpen(architectureSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select architecture"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  !(form.architecture || '').toString().trim()
              }"
            >
              <ng-option value="x86_64">x86_64</ng-option>
              <ng-option value="arm64">arm64</ng-option>
            </ng-select>
            <div
              *ngIf="
                wizardSubmitAttempted &&
                !(form.architecture || '').toString().trim()
              "
              class="field-error"
            >
              Architecture is required.
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="wizardStep === 2" class="ec2-launch-section">
        <div class="ec2-launch-section__title">Choose the instance type and access settings</div>
        <div class="ec2-launch-section__subtitle">
          Configure the machine size, key pair, IAM profile, and core launch behavior.
        </div>

        <div class="ec2-launch-grid">
          <div class="form-item ec2-launch-grid__full">
            <label>
              Instance Type <span class="required-star">*</span>
              <span *ngIf="loading.instanceTypes" class="tiny-loader"></span>
            </label>
            <ng-select #instanceTypeSelect dropdownPosition="bottom"
              class="instance-type-select instance-metadata-select"
              [items]="options.instanceTypes"
              bindLabel="instance_type"
              bindValue="instance_type"
              [(ngModel)]="form.instance_type"
              (open)="onLaunchModalDropdownOpen(instanceTypeSelect, 'request')"
              [searchable]="true"
              [loading]="loading.instanceTypes"
              [clearable]="true"
              placeholder="Select instance type"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  !(form.instance_type || '').toString().trim()
              }"
            >
              <ng-template ng-label-tmp let-item="item">
                <div class="instance-option">
                  <div class="instance-option__title">{{ item.instance_type }}</div>
                  <div class="instance-option__meta">
                    <span>vCPU: {{ item.vcpu ?? "-" }}</span>
                    <span>Memory: {{ item.memory || "-" }}</span>
                  </div>
                </div>
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                <div class="instance-option">
                  <div class="instance-option__title">{{ item.instance_type }}</div>
                  <div class="instance-option__meta">
                    <span>vCPU: {{ item.vcpu ?? "-" }}</span>
                    <span>Memory: {{ item.memory || "-" }}</span>
                  </div>
                </div>
              </ng-template>
            </ng-select>
            <div
              *ngIf="
                wizardSubmitAttempted &&
                !(form.instance_type || '').toString().trim()
              "
              class="field-error"
            >
              Instance type is required.
            </div>
          </div>

          <div class="form-item">
            <label>
              Key Name <span class="required-star">*</span>
              <span *ngIf="loading.keys" class="tiny-loader"></span>
            </label>
            <ng-select #keyNameSelect dropdownPosition="bottom"
              class="instance-type-select"
              [items]="options.keys"
              bindLabel="key_name"
              bindValue="key_name"
              [(ngModel)]="form.key_name"
              (open)="onLaunchModalDropdownOpen(keyNameSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select key"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted && !(form.key_name || '').toString().trim()
              }"
            ></ng-select>
            <div
              *ngIf="wizardSubmitAttempted && !(form.key_name || '').toString().trim()"
              class="field-error"
            >
              Key name is required.
            </div>
          </div>

          <div class="form-item">
            <label>
              IAM Instance Profile ARN <span class="required-star">*</span>
              <span *ngIf="loading.iamRoles" class="tiny-loader"></span>
            </label>
            <ng-select #iamProfileSelect dropdownPosition="bottom"
              class="instance-type-select"
              [items]="options.iamRoles"
              bindLabel="instance_profile_arn"
              bindValue="instance_profile_arn"
              [(ngModel)]="form.iam_instance_profile_arn"
              (open)="onLaunchModalDropdownOpen(iamProfileSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              [loading]="loading.iamRoles"
              placeholder="Select IAM profile"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  !(form.iam_instance_profile_arn || '').toString().trim()
              }"
            >
              <ng-template ng-footer-tmp>
                <div
                  *ngIf="iamRolesNextToken"
                  class="load-more-option cursor-pointer"
                  (click)="loadMoreIamRoles($event)"
                >
                  {{ loading.iamRoles ? "Loading..." : "Load more" }}
                </div>
              </ng-template>
            </ng-select>
            <div
              *ngIf="
                wizardSubmitAttempted &&
                !(form.iam_instance_profile_arn || '').toString().trim()
              "
              class="field-error"
            >
              IAM instance profile is required.
            </div>
          </div>

          <div class="form-item">
            <label>HTTP Tokens <span class="required-star">*</span></label>
            <ng-select #httpTokensSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.metadata_options.http_tokens"
              (open)="onLaunchModalDropdownOpen(httpTokensSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  !(form.metadata_options.http_tokens || '').toString().trim()
              }"
            >
              <ng-option value="required">required</ng-option>
              <ng-option value="optional">optional</ng-option>
            </ng-select>
          </div>

          <div class="form-item">
            <label>Monitoring Enabled <span class="required-star">*</span></label>
            <ng-select #monitoringSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.monitoring.enabled"
              (open)="onLaunchModalDropdownOpen(monitoringSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  form.monitoring.enabled !== true &&
                  form.monitoring.enabled !== false
              }"
            >
              <ng-option [value]="true">true</ng-option>
              <ng-option [value]="false">false</ng-option>
            </ng-select>
          </div>

          <div class="form-item">
            <label>Tenancy <span class="required-star">*</span></label>
            <ng-select #tenancySelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.placement.tenancy"
              (open)="onLaunchModalDropdownOpen(tenancySelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  !(form.placement.tenancy || '').toString().trim()
              }"
            >
              <ng-option value="default">shared</ng-option>
              <ng-option value="dedicated">dedicated</ng-option>
              <ng-option value="host">host</ng-option>
            </ng-select>
          </div>

          <div class="form-item">
            <label>EBS Optimized <span class="required-star">*</span></label>
            <ng-select #ebsOptimizedSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.ebs_optimized"
              (open)="onLaunchModalDropdownOpen(ebsOptimizedSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  form.ebs_optimized !== true &&
                  form.ebs_optimized !== false
              }"
            >
              <ng-option [value]="true">true</ng-option>
              <ng-option [value]="false">false</ng-option>
            </ng-select>
          </div>

          <div class="form-item">
            <label>Shutdown Behavior <span class="required-star">*</span></label>
            <ng-select #shutdownBehaviorSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.instance_initiated_shutdown_behavior"
              (open)="onLaunchModalDropdownOpen(shutdownBehaviorSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  !(form.instance_initiated_shutdown_behavior || '').toString().trim()
              }"
            >
              <ng-option value="stop">stop</ng-option>
              <ng-option value="terminate">terminate</ng-option>
            </ng-select>
          </div>
        </div>
      </div>

      <div *ngIf="wizardStep === 3" class="ec2-launch-section">
        <div class="ec2-launch-section__title">Configure network and security</div>
        <div class="ec2-launch-section__subtitle">
          Choose the VPC, subnet, security groups, and whether this instance should receive a public IP.
        </div>

        <div class="ec2-launch-grid">
          <div class="form-item">
            <label>
              VPC Id <span class="required-star">*</span>
              <span *ngIf="loading.vpcs" class="tiny-loader"></span>
            </label>
            <ng-select #vpcSelect dropdownPosition="bottom"
              class="instance-type-select"
              [items]="options.vpcs"
              bindLabel="vpc_id"
              bindValue="vpc_id"
              [(ngModel)]="form.vpc_id"
              (change)="onVpcChange()"
              (open)="onLaunchModalDropdownOpen(vpcSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select VPC"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted && !(form.vpc_id || '').toString().trim()
              }"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ getCreateSecurityGroupVpcLabel(item.vpc_id) }}
              </ng-template>
            </ng-select>
            <div
              *ngIf="wizardSubmitAttempted && !(form.vpc_id || '').toString().trim()"
              class="field-error"
            >
              VPC is required.
            </div>
          </div>

          <div class="form-item">
            <label>
              Subnet Id <span class="required-star">*</span>
              <span *ngIf="loading.subnets" class="tiny-loader"></span>
            </label>
            <ng-select #subnetSelect dropdownPosition="bottom"
              class="instance-type-select"
              [items]="options.subnets"
              bindLabel="subnet_id"
              bindValue="subnet_id"
              [(ngModel)]="form.subnet_id"
              (open)="onLaunchModalDropdownOpen(subnetSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select Subnet"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted && !(form.subnet_id || '').toString().trim()
              }"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ item.subnet_id }} {{ item.subnet_name ? '- (' + item.subnet_name + ')' : '' }}
              </ng-template>
            </ng-select>
            <div
              *ngIf="wizardSubmitAttempted && !(form.subnet_id || '').toString().trim()"
              class="field-error"
            >
              Subnet is required.
            </div>
          </div>

          <div class="form-item ec2-launch-grid__full">
            <div class="ec2-inline-action">
              <label class="ec2-inline-action__label">
                Security Group <span class="required-star">*</span>
                <span *ngIf="loading.securityGroups" class="tiny-loader"></span>
              </label>
              <button type="button" class="btn btn-light btn-sm" (click)="openCreateSecurityGroupForm()">
                <i class="fa fa-plus"></i> Create Security Group
              </button>
            </div>

            <ng-select #securityGroupSelect dropdownPosition="bottom"
              class="instance-type-select"
              [items]="options.securityGroups"
              bindLabel="security_group_name"
              bindValue="security_group_id"
              [(ngModel)]="form.security_group_ids"
              (open)="onLaunchModalDropdownOpen(securityGroupSelect, 'request')"
              [multiple]="true"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select security groups"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  (!form.security_group_ids || form.security_group_ids.length === 0)
              }"
            >
              <ng-template ng-option-tmp let-item="item">
                <div class="ec2-option-row">
                  <div class="ec2-option-row__title">
                    {{ getSecurityGroupDisplayName(item) }}
                  </div>
                  <div class="ec2-option-row__meta">
                    {{ getSecurityGroupDisplayId(item) }}
                  </div>
                </div>
              </ng-template>
            </ng-select>
            <div
              *ngIf="
                wizardSubmitAttempted &&
                (!form.security_group_ids || form.security_group_ids.length === 0)
              "
              class="field-error"
            >
              At least one security group is required.
            </div>

            <div
              *ngIf="getSelectedSecurityGroupOptions().length > 0"
              class="ec2-selection-grid"
            >
              <div
                *ngFor="let sg of getSelectedSecurityGroupOptions()"
                class="ec2-selection-card"
              >
                <div class="ec2-selection-card__title">
                  {{ getSecurityGroupDisplayName(sg) }}
                </div>
                <div class="ec2-selection-card__meta">
                  {{ getSecurityGroupDisplayId(sg) }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-item">
            <label>Associate Public IP <span class="required-star">*</span></label>
            <ng-select #publicIpSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.associate_public_ip_address"
              (open)="onLaunchModalDropdownOpen(publicIpSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
              [ngClass]="{
                'invalid-select':
                  wizardSubmitAttempted &&
                  form.associate_public_ip_address !== true &&
                  form.associate_public_ip_address !== false
              }"
            >
              <ng-option [value]="true">true</ng-option>
              <ng-option [value]="false">false</ng-option>
            </ng-select>
          </div>
        </div>
      </div>

      <div *ngIf="wizardStep === 4" class="ec2-launch-section">
        <div class="ec2-launch-section__title">Configure storage</div>
        <div class="ec2-launch-section__subtitle">
          The launch request supports a root device mapping and optional user data.
        </div>

        <div class="ec2-launch-grid">
          <div class="form-item">
            <label>Device Name</label>
            <input
              class="form-control"
              [(ngModel)]="form.block_device_mappings[0].device_name"
              placeholder="e.g. /dev/xvda"
            />
          </div>

          <div class="form-item">
            <label>Volume Size (GB)</label>
            <input
              type="number"
              class="form-control"
              [(ngModel)]="form.block_device_mappings[0].ebs.volume_size"
              placeholder="20"
            />
          </div>

          <div class="form-item">
            <label>Volume Type</label>
            <ng-select #volumeTypeSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.block_device_mappings[0].ebs.volume_type"
              (open)="onLaunchModalDropdownOpen(volumeTypeSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
            >
              <ng-option value="gp2">gp2</ng-option>
              <ng-option value="gp3">gp3</ng-option>
            </ng-select>
          </div>

          <div class="form-item">
            <label>Delete on Termination</label>
            <ng-select #deleteOnTerminationSelect dropdownPosition="bottom"
              class="instance-type-select"
              [(ngModel)]="form.block_device_mappings[0].ebs.delete_on_termination"
              (open)="onLaunchModalDropdownOpen(deleteOnTerminationSelect, 'request')"
              [searchable]="false"
              [clearable]="true"
              placeholder="Select"
            >
              <ng-option [value]="true">true</ng-option>
              <ng-option [value]="false">false</ng-option>
            </ng-select>
          </div>

          <div class="form-item ec2-launch-grid__full">
            <label>User Data</label>
            <textarea
              class="form-control ec2-textarea"
              [(ngModel)]="form.user_data"
              rows="5"
              placeholder="Optional bootstrap or cloud-init script"
            ></textarea>
          </div>
        </div>
      </div>

      <div *ngIf="wizardStep === 5" class="ec2-launch-section">
        <div class="ec2-launch-section__title">Name and tag the instance</div>
        <div class="ec2-launch-section__subtitle">
          Provide the instance name and the tag set that should be stored with the request.
        </div>

        <div class="ec2-launch-grid">
          <div class="form-item ec2-launch-grid__full">
            <label>Instance Name <span class="required-star">*</span></label>
            <input
              class="form-control"
              [(ngModel)]="form.instance_name"
              placeholder="e.g. app-prod-01"
              [ngClass]="{
                'field-invalid':
                  wizardSubmitAttempted &&
                  !(form.instance_name || '').toString().trim()
              }"
            />
            <div
              *ngIf="
                wizardSubmitAttempted && !(form.instance_name || '').toString().trim()
              "
              class="field-error"
            >
              Instance name is required.
            </div>
          </div>

          <div class="form-item ec2-launch-grid__full">
            <label>Tags <span class="required-star">*</span></label>
            <div *ngFor="let tag of form.tags; let i = index" class="tag-row">
              <input
                class="form-control"
                placeholder="key"
                [(ngModel)]="tag.key"
                [ngClass]="{
                  'field-invalid':
                    wizardSubmitAttempted && !(tag.key || '').toString().trim()
                }"
              />
              <input
                class="form-control"
                placeholder="value"
                [(ngModel)]="tag.value"
                [ngClass]="{
                  'field-invalid':
                    wizardSubmitAttempted && !(tag.value || '').toString().trim()
                }"
              />
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm tag-remove"
                (click)="removeTag(i)"
              >
                X
              </button>
            </div>
            <div *ngIf="wizardSubmitAttempted && !form.tags?.length" class="field-error">
              At least one tag is required.
            </div>
            <button type="button" class="btn btn-light btn-sm ec2-add-tag-btn" (click)="addTag()">
              <i class="fa fa-plus"></i> Add Tag
            </button>
          </div>
        </div>
      </div>

      <div *ngIf="wizardStep === 6" class="ec2-launch-section">
        <div class="ec2-launch-section__title">Review and submit</div>
        <div class="ec2-launch-section__subtitle">
          Pricing is loaded from the same launch request API before the request is saved.
        </div>

        <div *ngIf="pricingLoading" class="ec2-review-banner">
          Fetching pricing details...
        </div>
        <div *ngIf="!pricingLoading && pricingError" class="ec2-review-banner ec2-review-banner--error">
          {{ pricingError }}
          <button type="button" class="btn btn-light btn-sm" (click)="refreshReview()">
            Retry
          </button>
        </div>
        <div *ngIf="!pricingLoading && pricing" class="ec2-pricing-strip">
          <div class="ec2-pricing-strip__item">
            <span>Hourly</span>
            <strong>{{ pricing.hourly_cost_usd }} {{ pricing.currency }}</strong>
          </div>
          <div class="ec2-pricing-strip__item">
            <span>Monthly</span>
            <strong>{{ pricing.monthly_cost_usd }} {{ pricing.currency }}</strong>
          </div>
        </div>

        <div *ngIf="saveSuccessMessage" class="alert alert-success save-success-message mt-2">
          <div>{{ saveSuccessMessage }}</div>
          <div *ngIf="saveRequestId" class="mt-1">Request ID: {{ saveRequestId }}</div>
        </div>

        <div *ngIf="saveError" class="alert alert-danger save-error-message mt-2">
          {{ saveError }}
        </div>

        <div class="ec2-review-grid">
          <div class="ec2-review-card">
            <div class="ec2-review-card__title">Request Summary</div>
            <div class="ec2-review-row">
              <span>Operating System</span>
              <strong>{{ form.operating_system || "-" }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>AMI</span>
              <strong>{{ getSelectedAmiOption()?.ami_name || form.ami_id || "-" }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>Instance Type</span>
              <strong>{{ form.instance_type || "-" }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>VPC</span>
              <strong>{{ getCreateSecurityGroupVpcLabel(form.vpc_id) }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>Subnet</span>
              <strong>{{ getSubnetDisplayLabel() }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>Public IP</span>
              <strong>{{ form.associate_public_ip_address }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>Security Groups</span>
              <strong>{{ form.security_group_ids?.length || 0 }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>Root Volume</span>
              <strong>{{ getVolumeSummary() }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>Instance Name</span>
              <strong>{{ form.instance_name || "-" }}</strong>
            </div>
            <div class="ec2-review-row">
              <span>IAM Profile</span>
              <strong>{{ form.iam_instance_profile_arn || "-" }}</strong>
            </div>
          </div>

          <div class="ec2-review-card">
            <div class="ec2-review-card__title">Tags and Notes</div>
            <div class="ec2-review-stack">
              <div class="ec2-review-note">
                {{ form.ec2_launch_justification || "-" }}
              </div>
              <div class="ec2-review-tags">
                <div *ngFor="let tag of form.tags" class="ec2-review-tag">
                  <span>{{ tag.key }}</span>
                  <strong>{{ tag.value }}</strong>
                </div>
              </div>
              <div *ngIf="hasAnyUserData()" class="ec2-review-note ec2-review-note--muted">
                User data script attached
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ec2-launch-modal__footer">
      <button
        *ngIf="wizardStep === 1"
        type="button"
        class="btn btn-light ec2-footer-btn"
        [disabled]="savingRequest"
        (click)="requestClose()"
      >
        Cancel
      </button>
      <button
        *ngIf="wizardStep > 1"
        type="button"
        class="btn btn-light ec2-footer-btn"
        [disabled]="savingRequest"
        (click)="previousWizardStep()"
      >
        <i class="fa fa-arrow-left"></i> Back
      </button>

      <div class="ec2-launch-modal__footer-meta">
        Step {{ wizardStep }} of {{ wizardStepLabels.length }}
      </div>

      <button
        *ngIf="wizardStep < wizardStepLabels.length"
        type="button"
        class="btn ec2-footer-btn ec2-footer-btn--primary"
        [disabled]="savingRequest"
        (click)="nextWizardStep()"
      >
        Next <i class="fa fa-arrow-right"></i>
      </button>

      <button
        *ngIf="wizardStep === wizardStepLabels.length"
        type="button"
        class="btn ec2-footer-btn ec2-footer-btn--accent"
        [disabled]="savingRequest || pricingLoading || !pricing || !!pricingError"
        (click)="launchRequest()"
      >
        <span *ngIf="savingRequest" class="spinner-border spinner-border-sm me-2"></span>
        {{ savingRequest ? "Submitting..." : "Launch EC2" }}
      </button>
    </div>
  </div>
</div>

<div *ngIf="showCreateSecurityGroupForm" class="sg-modal-backdrop">
  <div class="sg-modal">
    <div class="sg-modal__header">
      <div class="sg-modal__title-wrap">
        <div class="sg-modal__badge">
          <i class="fa fa-shield"></i>
        </div>
        <div class="sg-create-panel__title">Create Security Group</div>
      </div>
      <button
        type="button"
        class="sg-modal__close"
        [disabled]="loading.createSecurityGroup"
        title="Close"
        (click)="closeCreateSecurityGroupForm()"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div #securityGroupModalBody class="sg-modal__body">
      <div class="sg-stepper">
        <div
          *ngFor="let stepLabel of createSecurityGroupStepLabels; let i = index"
          class="sg-stepper__item"
          [ngClass]="{
            'is-active': createSecurityGroupStep === i + 1,
            'is-complete': createSecurityGroupStep > i + 1
          }"
        >
          <div class="sg-stepper__circle">
            <ng-container *ngIf="createSecurityGroupStep > i + 1; else sgStepNumber">
              <i class="fa fa-check"></i>
            </ng-container>
            <ng-template #sgStepNumber>{{ i + 1 }}</ng-template>
          </div>
          <div class="sg-stepper__label">{{ stepLabel }}</div>
        </div>
      </div>

      <div *ngIf="createSecurityGroupStep === 1" class="sg-create-section">
        <div class="sg-step-panel__title">Basic details</div>
        <div class="sg-step-panel__subtitle">
          The security group's name and VPC scope. Description is required by AWS.
        </div>

        <div class="sg-create-grid">
          <div class="form-item">
            <label>Name <span class="required-star">*</span></label>
            <input
              class="form-control"
              [ngClass]="{
                'field-invalid':
                  createSecurityGroupSubmitAttempted &&
                  !(createSecurityGroupForm.group_name || '').toString().trim()
              }"
              [(ngModel)]="createSecurityGroupForm.group_name"
              placeholder="e.g. web-tier-sg"
            />
            <div
              *ngIf="
                createSecurityGroupSubmitAttempted &&
                !(createSecurityGroupForm.group_name || '').toString().trim()
              "
              class="field-error"
            >
              Security group name is required.
            </div>
          </div>

          <div class="form-item sg-create-grid__full">
            <label>Description <span class="required-star">*</span></label>
            <input
              class="form-control"
              [ngClass]="{
                'field-invalid':
                  createSecurityGroupSubmitAttempted &&
                  !(createSecurityGroupForm.description || '').toString().trim()
              }"
              [(ngModel)]="createSecurityGroupForm.description"
              placeholder="What this SG protects"
            />
            <div
              *ngIf="
                createSecurityGroupSubmitAttempted &&
                !(createSecurityGroupForm.description || '').toString().trim()
              "
              class="field-error"
            >
              Description is required.
            </div>
          </div>

          <div class="form-item sg-create-grid__full">
            <label>VPC <span class="required-star">*</span></label>
            <ng-select #createSecurityGroupVpcSelect dropdownPosition="bottom"
              class="instance-type-select"
              [ngClass]="{
                'invalid-select':
                  createSecurityGroupSubmitAttempted &&
                  !(createSecurityGroupForm.vpc_id || '').toString().trim()
              }"
              [items]="options.vpcs"
              bindLabel="vpc_id"
              bindValue="vpc_id"
              [(ngModel)]="createSecurityGroupForm.vpc_id"
              (open)="onLaunchModalDropdownOpen(createSecurityGroupVpcSelect, 'securityGroup')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select VPC"
              notFoundText="No VPC found"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ getCreateSecurityGroupVpcLabel(item.vpc_id) }}
              </ng-template>
            </ng-select>
            <div
              *ngIf="
                createSecurityGroupSubmitAttempted &&
                !(createSecurityGroupForm.vpc_id || '').toString().trim()
              "
              class="field-error"
            >
              VPC is required.
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="createSecurityGroupStep === 2" class="sg-create-section">
        <div class="sg-step-panel__title">Inbound rules</div>
        <div class="sg-step-panel__subtitle">
          Allow traffic INTO resources attached to this group. Critical ports (22 SSH, 3389 RDP)
          open to 0.0.0.0/0 are flagged.
        </div>

        <div class="sg-rule-editor">
          <div class="sg-rule-editor__header">
            <span>Protocol</span>
            <span>Port</span>
            <span>Source</span>
            <span>Description</span>
            <span></span>
          </div>

          <div
            *ngFor="let rule of createSecurityGroupForm.inbound_rules; let i = index"
            class="sg-rule-block"
          >
            <div class="sg-rule-row">
              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Protocol</label>
                <select
                  class="form-control"
                  [(ngModel)]="rule.protocol"
                  (ngModelChange)="onCreateSecurityGroupRuleProtocolChange(rule)"
                >
                  <option *ngFor="let protocol of createSecurityGroupProtocolOptions" [value]="protocol">
                    {{ protocol }}
                  </option>
                </select>
              </div>

              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Port</label>
                <input
                  class="form-control"
                  [ngClass]="{
                    'field-invalid':
                      createSecurityGroupSubmitAttempted &&
                      !isCreateSecurityGroupRuleValid(rule, 'inbound')
                  }"
                  [(ngModel)]="rule.port"
                  [disabled]="!usesCreateSecurityGroupRulePort(rule.protocol)"
                  [placeholder]="usesCreateSecurityGroupRulePort(rule.protocol) ? '443' : '-'"
                />
              </div>

              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Source</label>
                <input
                  class="form-control"
                  [ngClass]="{
                    'field-invalid':
                      createSecurityGroupSubmitAttempted &&
                      !isCreateSecurityGroupRuleValid(rule, 'inbound')
                  }"
                  [(ngModel)]="rule.target"
                  placeholder="0.0.0.0/0"
                />
              </div>

              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Description</label>
                <input
                  class="form-control"
                  [ngClass]="{
                    'field-invalid':
                      createSecurityGroupSubmitAttempted &&
                      !isCreateSecurityGroupRuleValid(rule, 'inbound')
                  }"
                  [(ngModel)]="rule.description"
                  placeholder="HTTPS"
                />
              </div>

              <button
                type="button"
                class="sg-rule-remove"
                (click)="removeCreateSecurityGroupRule('inbound', i)"
                title="Remove inbound rule"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <div
              *ngIf="
                createSecurityGroupSubmitAttempted &&
                !isCreateSecurityGroupRuleValid(rule, 'inbound')
              "
              class="field-error"
            >
              {{ getCreateSecurityGroupRuleValidationMessage(rule, 'inbound') }}
            </div>

            <div *ngIf="isCreateSecurityGroupRuleFlagged(rule)" class="sg-rule-warning">
              This inbound rule opens SSH or RDP access to the world.
            </div>
          </div>
        </div>

        <button type="button" class="sg-add-rule-btn" (click)="addCreateSecurityGroupRule('inbound')">
          <i class="fa fa-plus"></i> Add inbound rule
        </button>
      </div>

      <div *ngIf="createSecurityGroupStep === 3" class="sg-create-section">
        <div class="sg-step-panel__title">Outbound rules</div>
        <div class="sg-step-panel__subtitle">
          Allow traffic OUT FROM resources attached to this group.
        </div>

        <div class="sg-rule-editor">
          <div class="sg-rule-editor__header">
            <span>Protocol</span>
            <span>Port</span>
            <span>Destination</span>
            <span>Description</span>
            <span></span>
          </div>

          <div
            *ngFor="let rule of createSecurityGroupForm.outbound_rules; let i = index"
            class="sg-rule-block"
          >
            <div class="sg-rule-row">
              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Protocol</label>
                <select
                  class="form-control"
                  [(ngModel)]="rule.protocol"
                  (ngModelChange)="onCreateSecurityGroupRuleProtocolChange(rule)"
                >
                  <option *ngFor="let protocol of createSecurityGroupProtocolOptions" [value]="protocol">
                    {{ protocol }}
                  </option>
                </select>
              </div>

              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Port</label>
                <input
                  class="form-control"
                  [ngClass]="{
                    'field-invalid':
                      createSecurityGroupSubmitAttempted &&
                      !isCreateSecurityGroupRuleValid(rule, 'outbound')
                  }"
                  [(ngModel)]="rule.port"
                  [disabled]="!usesCreateSecurityGroupRulePort(rule.protocol)"
                  [placeholder]="usesCreateSecurityGroupRulePort(rule.protocol) ? '443' : '-'"
                />
              </div>

              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Destination</label>
                <input
                  class="form-control"
                  [ngClass]="{
                    'field-invalid':
                      createSecurityGroupSubmitAttempted &&
                      !isCreateSecurityGroupRuleValid(rule, 'outbound')
                  }"
                  [(ngModel)]="rule.target"
                  placeholder="0.0.0.0/0"
                />
              </div>

              <div class="sg-rule-field">
                <label class="sg-rule-field__mobile-label">Description</label>
                <input
                  class="form-control"
                  [ngClass]="{
                    'field-invalid':
                      createSecurityGroupSubmitAttempted &&
                      !isCreateSecurityGroupRuleValid(rule, 'outbound')
                  }"
                  [(ngModel)]="rule.description"
                  placeholder="Allow all outbound"
                />
              </div>

              <button
                type="button"
                class="sg-rule-remove"
                (click)="removeCreateSecurityGroupRule('outbound', i)"
                title="Remove outbound rule"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <div
              *ngIf="
                createSecurityGroupSubmitAttempted &&
                !isCreateSecurityGroupRuleValid(rule, 'outbound')
              "
              class="field-error"
            >
              {{ getCreateSecurityGroupRuleValidationMessage(rule, 'outbound') }}
            </div>
          </div>
        </div>

        <button type="button" class="sg-add-rule-btn" (click)="addCreateSecurityGroupRule('outbound')">
          <i class="fa fa-plus"></i> Add outbound rule
        </button>
      </div>

      <div *ngIf="createSecurityGroupStep === 4" class="sg-create-section">
        <div class="sg-step-panel__title">Tag the security group</div>

        <div class="form-item sg-tag-field">
          <label>Environment</label>
          <ng-select #createSecurityGroupEnvironmentSelect dropdownPosition="bottom"
            class="instance-type-select"
            [clearable]="false"
            [searchable]="false"
            [(ngModel)]="createSecurityGroupForm.environment"
            (open)="onLaunchModalDropdownOpen(createSecurityGroupEnvironmentSelect, 'securityGroup')"
          >
            <ng-option
              *ngFor="let environment of createSecurityGroupEnvironmentOptions"
              [value]="environment"
            >
              {{ environment }}
            </ng-option>
          </ng-select>
        </div>
      </div>

      <div *ngIf="createSecurityGroupStep === 5" class="sg-create-section">
        <div class="sg-review-banner">
          The security group will be created first, then the inbound and outbound rules will be applied.
        </div>

        <div class="sg-review-card">
          <div class="sg-review-row">
            <span>Name</span>
            <strong>{{ createSecurityGroupForm.group_name || "-" }}</strong>
          </div>
          <div class="sg-review-row">
            <span>Description</span>
            <strong>{{ createSecurityGroupForm.description || "-" }}</strong>
          </div>
          <div class="sg-review-row">
            <span>VPC</span>
            <strong>{{ getCreateSecurityGroupVpcLabel(createSecurityGroupForm.vpc_id) }}</strong>
          </div>
          <div class="sg-review-row">
            <span>Inbound rules</span>
            <strong>{{ getCreateSecurityGroupRuleCountLabel('inbound') }}</strong>
          </div>
          <div class="sg-review-row">
            <span>Outbound rules</span>
            <strong>{{ getCreateSecurityGroupRuleCountLabel('outbound') }}</strong>
          </div>
          <div class="sg-review-row">
            <span>Environment</span>
            <strong>{{ createSecurityGroupForm.environment || "-" }}</strong>
          </div>
        </div>

        <div class="sg-review-rules">
          <div class="sg-review-rules__title">Inbound:</div>
          <ul>
            <li *ngFor="let rule of createSecurityGroupForm.inbound_rules">
              {{ formatCreateSecurityGroupRuleForReview(rule, 'inbound') }}
            </li>
          </ul>

          <div class="sg-review-rules__title">Outbound:</div>
          <ul>
            <li *ngFor="let rule of createSecurityGroupForm.outbound_rules">
              {{ formatCreateSecurityGroupRuleForReview(rule, 'outbound') }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="sg-modal__footer">
      <div class="sg-create-actions">
        <button
          *ngIf="createSecurityGroupStep === 1"
          type="button"
          class="btn btn-light"
          [disabled]="loading.createSecurityGroup"
          (click)="closeCreateSecurityGroupForm()"
        >
          Cancel
        </button>
        <button
          *ngIf="createSecurityGroupStep > 1"
          type="button"
          class="btn btn-light"
          [disabled]="loading.createSecurityGroup"
          (click)="previousCreateSecurityGroupStep()"
        >
          &larr; Back
        </button>

        <div class="sg-create-actions__meta">
          Step {{ createSecurityGroupStep }} of {{ createSecurityGroupStepLabels.length }}
        </div>

        <button
          *ngIf="createSecurityGroupStep < createSecurityGroupStepLabels.length"
          type="button"
          class="btn btn-primary sg-next-btn"
          [disabled]="loading.createSecurityGroup"
          (click)="nextCreateSecurityGroupStep()"
        >
          Next &rarr;
        </button>
        <button
          *ngIf="createSecurityGroupStep === createSecurityGroupStepLabels.length"
          type="button"
          class="btn sg-create-submit-btn"
          [disabled]="loading.createSecurityGroup"
          (click)="submitCreateSecurityGroup()"
        >
          <span *ngIf="loading.createSecurityGroup" class="spinner-border spinner-border-sm me-2"></span>
          {{ loading.createSecurityGroup ? "Creating..." : "Create security group" }}
        </button>
      </div>
    </div>
  </div>
</div>
